/**
 * Version-negotiation FOUNDATION (docs/332-MOBILE-ADMIN-APP-CROSS-PROVIDER.md v1 §2/§4.1) —
 * a pure, coverage-gated resolver deciding which admin operations the admin app may offer
 * against a GIVEN provider. This is the crux of docs/332's cross-provider plan: a
 * second/third provider an owner (or a ministry) connects the admin app to may run an
 * OLDER `contractVersion` that lacks a newer admin endpoint (the flagship example is
 * password reset, docs/323, which needs `≥ 2.30.0`) — the admin app must degrade the
 * feature set gracefully rather than hard-failing or showing a broken button.
 *
 * Design (docs/332 §2.2, the "hybrid" recommendation): **v1 ships `contractVersion`
 * semver-gating only** — one field on `GET /provider`
 * (`packages/core/providers/providerMetadata.ts`) compared against a per-capability
 * `MIN_VERSION` table via the existing `semverCompare`/`semverGte` (`../core/semver`,
 * docs/108 — reused, not re-implemented). An explicit server-advertised
 * `capabilities: string[]` list (§2.2's v2 half, for backport/partial-impl robustness)
 * and an idempotent-read endpoint-probe downgrade path are both already modeled in
 * {@link AdminCapabilityInput} so a later `GET /provider` addition needs no reshaping of
 * this resolver's own precedence rules — only real callers wiring `capabilities`/`probed`
 * in. Per docs/332 §2.2: **never probe a mutation** — a probe result may only ever
 * DOWNGRADE a capability to unavailable (e.g. a cached `404`), never upgrade one a version
 * check alone wouldn't already grant.
 *
 * Precedence per capability (docs/332 §2.3):
 *   1. An explicit `capabilities` list, when the provider sends one, is authoritative —
 *      "absence means unavailable" for anything not listed, but a listed capability can
 *      also grant support EARLIER than the version table would (a backport).
 *   2. Otherwise, `contractVersion >= MIN_VERSION[cap]` (semver-gated).
 *   3. A cached `probed[cap] === false` always downgrades to unavailable, regardless of
 *      (1)/(2); `probed[cap] === true` is a no-op (it can never upgrade).
 *
 * A provider with NO `contractVersion` at all (an older provider, or one that predates
 * this Worker's docs/332 change) is classified `legacy` — it still resolves capabilities
 * through the SAME version-gate math (an absent version is treated as the effective floor
 * `"0.0.0"`, which `semverGte` already yields for any malformed/missing string, docs/108),
 * so it lands on exactly the documented legacy-safe baseline (docs/332 §2.4/§8 Q8):
 * `users.list`, `invites.list`, `invites.create`, `grants.manage`, `users.setRole` — the
 * oldest, pre-contract-versioning admin surface — and nothing newer, e.g. no password
 * reset. `legacy` is exposed separately so the UI can show a non-fatal "Limited — older
 * provider" badge (docs/332 §2.4) without having to re-derive it from the capability set.
 */
import { semverCompare, semverGte } from "../core/semver";

/**
 * The docs/332 §4.1 minimal cross-provider feature set (v1 scope) — the "simple,
 * oldest, universally-available" operations the owner's plan names explicitly:
 * seeing users, invite codes, password resets, grants, and role changes. The
 * heavier/tenant-specific admin surface (catalogs, manifests, devices, usage,
 * provider-config, moderation, …) stays owner/single-provider-only in v1 and is
 * NOT modeled here — see docs/332 §4.1's own table for the full list this
 * resolver is deliberately scoped narrower than.
 */
export type AdminCapabilityId =
  | "users.list"
  | "users.setRole"
  | "users.setPassword"
  | "invites.list"
  | "invites.create"
  | "grants.manage";

/** Every {@link AdminCapabilityId} this resolver knows about, in table order. */
export const ADMIN_CAPABILITY_IDS: readonly AdminCapabilityId[] = [
  "users.list",
  "users.setRole",
  "users.setPassword",
  "invites.list",
  "invites.create",
  "grants.manage",
];

/** The pre-contract-versioning floor — "always available, even on a legacy/no-version
 * provider" (docs/332 §2.4/§8 Q8). `semverGte(x, "0.0.0")` is true for any real version
 * string AND for the "0.0.0" a missing/malformed `contractVersion` itself resolves to
 * (`semverCompare`'s documented never-throws fallback, docs/108) — so this floor doubles
 * as the legacy-provider baseline with no special-case branch needed below. */
const LEGACY_BASELINE = "0.0.0";

/**
 * The durable record of "which contract version introduced this admin endpoint" —
 * cross-check against `specs/shared/PROVIDER-CONTRACT.md`'s own version-bump log
 * (docs/332 §2.3). Keep this table, not scattered `semverGte` calls in screens, as the
 * one source of truth both the RN admin and the CoreUI web admin (docs/325) render from.
 */
export const ADMIN_CAPABILITY_MIN_VERSION: Readonly<Record<AdminCapabilityId, string>> = {
  "users.list": LEGACY_BASELINE,
  "users.setRole": LEGACY_BASELINE,
  // docs/323-ADMIN-CREDENTIAL-MANAGEMENT.md v0 — POST /admin/users/set-password.
  // The flagship capability-gated case docs/332 calls out explicitly: a provider on
  // 2.29.x simply has no such route, so this must gate below its version, not above.
  "users.setPassword": "2.30.0",
  "invites.list": LEGACY_BASELINE,
  "invites.create": LEGACY_BASELINE,
  "grants.manage": LEGACY_BASELINE,
};

/**
 * The highest major version this admin build knows how to speak to at all — used only
 * for the advisory `providerTooNew` warning (docs/332 §2.2 "warn on an unsupported
 * future major version"), never to withhold a capability outright: a genuinely newer
 * provider is still very likely backward-compatible at the wire-shape level (semver's
 * own convention), so capabilities still resolve via the normal version-gate math even
 * when `providerTooNew` is true.
 */
const APP_MAX_KNOWN_MAJOR = 2;

/** Best-effort leading major-version number out of a semver-ish string; unparsable/absent
 * input yields `0` (never throws — mirrors `semver.ts`'s own never-throws discipline). */
function majorVersion(version: string): number {
  const m = /^\s*(\d+)\./.exec(version);
  return m ? Number(m[1]) : 0;
}

/** A cached probe result for one capability — see the module doc's "probe" precedence
 * rule. Only ever consulted to DOWNGRADE; a `true` entry is a no-op. */
export type AdminCapabilityProbe = Partial<Record<AdminCapabilityId, boolean>>;

export interface AdminCapabilityInput {
  /** From `GET /provider`'s `contractVersion` (`providerMetadata.ts`). `undefined` means
   * the provider didn't advertise one at all — treated as `legacy` (see module doc). */
  contractVersion?: string;
  /** From a future `GET /provider` `capabilities: string[]` (docs/332 v2, §4.2) — not
   * built server-side yet, but the resolver already honors it when a caller supplies
   * one, so wiring v2 needs no resolver reshaping. `undefined` (today's reality for
   * every real provider) falls through to pure version-gating. */
  capabilities?: readonly string[];
  /** Cached results of a defensive idempotent-read probe (docs/332 §2.2) — e.g. a `404`
   * on a `GET`-only capability check. Downgrade-only, see the module doc. */
  probed?: AdminCapabilityProbe;
}

export interface AdminCapabilitiesResult {
  /** Which of {@link ADMIN_CAPABILITY_IDS} are usable against this provider right now. */
  supported: ReadonlySet<AdminCapabilityId>;
  /** What this admin BUILD itself understands at most — the highest entry in
   * {@link ADMIN_CAPABILITY_MIN_VERSION}, a constant derived from the table (not
   * provider-specific), useful for an "this admin app supports contract features up
   * to vX" info line. */
  minClientContractVersion: string;
  /** The provider advertised a `contractVersion` whose MAJOR exceeds
   * {@link APP_MAX_KNOWN_MAJOR} — an advisory warning only (docs/332 §2.2), never a
   * capability blanket-block; capabilities still resolve normally alongside this flag. */
  providerTooNew: boolean;
  /** True when the provider advertised NO `contractVersion` at all (an older provider,
   * or a third-party implementation that predates this field) — drives the "Limited —
   * older provider" badge (docs/332 §2.4). Functionally this provider still resolves
   * exactly the legacy-safe baseline via the normal version-gate math (see module doc);
   * this flag exists purely so the UI doesn't have to re-derive "was it a real version
   * string or nothing at all" itself. */
  legacy: boolean;
}

/**
 * Resolve which admin capabilities are usable against a provider whose `GET /provider`
 * response (or lack of one) produced `input`. Never throws — every branch degrades to a
 * safe (i.e. more restrictive) result, matching this whole module's fail-open-but-honest
 * posture (docs/332 §2.4: "connect anyway; degrade the feature set").
 */
export function resolveAdminCapabilities(input: AdminCapabilityInput = {}): AdminCapabilitiesResult {
  const rawVersion = input.contractVersion?.trim();
  const legacy = !rawVersion;
  const effectiveVersion = rawVersion || LEGACY_BASELINE;
  const providerTooNew = !!rawVersion && majorVersion(rawVersion) > APP_MAX_KNOWN_MAJOR;

  const capabilityList = input.capabilities;
  const supported = new Set<AdminCapabilityId>();

  for (const id of ADMIN_CAPABILITY_IDS) {
    // Precedence step 1/2: an explicit capability list (when the provider sends one) is
    // authoritative — otherwise fall back to the version-gate table.
    let ok = capabilityList ? capabilityList.includes(id) : semverGte(effectiveVersion, ADMIN_CAPABILITY_MIN_VERSION[id]);
    // Precedence step 3: a cached probe result may only ever downgrade.
    if (input.probed?.[id] === false) ok = false;
    if (ok) supported.add(id);
  }

  return {
    supported,
    minClientContractVersion: MIN_CLIENT_CONTRACT_VERSION,
    providerTooNew,
    legacy,
  };
}

/** Derived once, not hand-maintained — the highest `MIN_VERSION` table entry. */
const MIN_CLIENT_CONTRACT_VERSION = ADMIN_CAPABILITY_IDS.reduce(
  (max, id) => (semverCompare(ADMIN_CAPABILITY_MIN_VERSION[id], max) > 0 ? ADMIN_CAPABILITY_MIN_VERSION[id] : max),
  LEGACY_BASELINE,
);

/**
 * Convenience selector bridging the actual provider-metadata flow
 * (`BackendClient.providerMetadata()` → `ProviderMetadata`) straight into
 * {@link resolveAdminCapabilities}, so a caller never has to hand-pluck
 * `contractVersion` off the parsed metadata itself. This is the "make the value
 * resolvable + available" wiring docs/332's v1 slice asks for — the full multi-provider
 * switcher UI that consumes it (hiding/disabling tabs per provider) is deferred to
 * docs/332's own v1-rest/v2 phasing, not built here.
 */
export function resolveAdminCapabilitiesFromMetadata(
  metadata: { contractVersion?: string },
  extra: Omit<AdminCapabilityInput, "contractVersion"> = {},
): AdminCapabilitiesResult {
  return resolveAdminCapabilities({ ...extra, contractVersion: metadata.contractVersion });
}
